import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BusinessPartnerService, {
  BusinessPartner,
  UpdateBusinessPartnerPayload,
} from '../services/BusinessPartnerService';

interface ContactProfileTabProps {
  contact: BusinessPartner;
  onContactUpdated: () => void;
}

export default function ContactProfileTab({
  contact,
  onContactUpdated,
}: ContactProfileTabProps): React.JSX.Element {
  const [formData, setFormData] = useState<UpdateBusinessPartnerPayload>({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData({
      company_name: contact.company_name || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      street: contact.street || '',
      house_no: contact.house_no || '',
      postal_code: contact.postal_code || '',
      city: contact.city || '',
      country: contact.country || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      website: contact.website || '',
      tax_number: contact.tax_number || '',
      vat_id: contact.vat_id || '',
      notes: contact.notes || '',
    });
    setHasChanges(false);
  }, [contact]);

  const handleChange = (key: keyof UpdateBusinessPartnerPayload, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await BusinessPartnerService.update(contact.id, formData);
      toast.success('Contact updated');
      setHasChanges(false);
      onContactUpdated();
    } catch (err: any) {
      console.error('Failed to save contact:', err);
      const message = err?.response?.data?.detail || 'Save failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      company_name: contact.company_name || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      street: contact.street || '',
      house_no: contact.house_no || '',
      postal_code: contact.postal_code || '',
      city: contact.city || '',
      country: contact.country || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      website: contact.website || '',
      tax_number: contact.tax_number || '',
      vat_id: contact.vat_id || '',
      notes: contact.notes || '',
    });
    setHasChanges(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e3ef',
    borderRadius: '6px',
    fontSize: '14px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#444',
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Type (read-only) */}
      <div style={{ marginBottom: '24px', padding: '16px', background: '#f8f9fc', borderRadius: '8px' }}>
        <label style={labelStyle}>Type</label>
        <input
          type="text"
          value={BusinessPartnerService.getKindLabel(contact.kind)}
          readOnly
          style={{ ...inputStyle, background: '#eee', cursor: 'not-allowed' }}
        />
      </div>

      {/* Company / Person Info */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#021a4a' }}>
          {contact.is_company ? 'Company details' : 'Personal details'}
        </h3>

        {contact.is_company && (
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Company name</label>
            <input
              type="text"
              value={formData.company_name || ''}
              onChange={(e) => handleChange('company_name', e.target.value)}
              style={inputStyle}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>First name</label>
            <input
              type="text"
              value={formData.first_name || ''}
              onChange={(e) => handleChange('first_name', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Last name</label>
            <input
              type="text"
              value={formData.last_name || ''}
              onChange={(e) => handleChange('last_name', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#021a4a' }}>
          Address
        </h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>Street</label>
            <input
              type="text"
              value={formData.street || ''}
              onChange={(e) => handleChange('street', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>No.</label>
            <input
              type="text"
              value={formData.house_no || ''}
              onChange={(e) => handleChange('house_no', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Postal code</label>
            <input
              type="text"
              value={formData.postal_code || ''}
              onChange={(e) => handleChange('postal_code', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>City</label>
            <input
              type="text"
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Country</label>
          <input
            type="text"
            value={formData.country || ''}
            onChange={(e) => handleChange('country', e.target.value)}
            placeholder="Germany"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Contact Info */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#021a4a' }}>
          Contact details
        </h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Website</label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://..."
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Phone</label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Mobile</label>
            <input
              type="tel"
              value={formData.mobile || ''}
              onChange={(e) => handleChange('mobile', e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Tax Info */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#021a4a' }}>
          Tax information
        </h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Tax number</label>
            <input
              type="text"
              value={formData.tax_number || ''}
              onChange={(e) => handleChange('tax_number', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>VAT ID</label>
            <input
              type="text"
              value={formData.vat_id || ''}
              onChange={(e) => handleChange('vat_id', e.target.value)}
              placeholder="DE123456789"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#021a4a' }}>
          Notes
        </h3>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
          placeholder="Internal notes about this contact..."
        />
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          paddingTop: '24px',
          borderTop: '1px solid #e0e3ef',
        }}
      >
        <button
          onClick={handleCancel}
          disabled={!hasChanges || saving}
          style={{
            padding: '10px 24px',
            border: '1px solid #e0e3ef',
            borderRadius: '6px',
            background: 'white',
            color: '#666',
            fontSize: '14px',
            fontWeight: 600,
            cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
            opacity: hasChanges && !saving ? 1 : 0.5,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: '6px',
            background: hasChanges && !saving ? '#021a4a' : '#ccc',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
